import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../../config/api';
import {
  FaCalendarCheck,
  FaUsers,
  FaRupeeSign,
  FaPercent,
  FaChartBar,
  FaChartLine,
} from 'react-icons/fa';
import './AdminDashboard.css';

const RUPEE_SYMBOL = '\u20B9';

const formatCurrency = (amount) => `${RUPEE_SYMBOL}${Number(amount ?? 0).toLocaleString('en-IN')}`;

const formatCompactCurrency = (amount) =>
  `${RUPEE_SYMBOL}${new Intl.NumberFormat('en-IN', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(amount ?? 0))}`;

const formatChartDay = (isoDate) =>
  new Date(`${isoDate}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      setError(null);
      const res = await fetch(getApiUrl('/api/admin/dashboard'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) setData(json.dashboard);
      else setError(json.message || 'Failed to load');
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!data) return;
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, [data, fetchDashboard]);

  if (loading && !data) {
    return (
      <div className="admin-page-loading">
        <div className="admin-spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="admin-page-error">
        <p>{error}</p>
      </div>
    );
  }

  const d = data || {};
  const statusCounts = d.bookingStatus || {};
  const revenueTrend = Array.isArray(d.revenueLast14Days) ? d.revenueLast14Days : [];
  const revenueTrendTotal = revenueTrend.reduce((sum, point) => sum + Number(point.revenue || 0), 0);
  const revenueTrendAverage = revenueTrend.length ? Math.round(revenueTrendTotal / revenueTrend.length) : 0;
  const revenueTrendMax = revenueTrend.reduce((max, point) => Math.max(max, Number(point.revenue || 0)), 0);
  const bestRevenueDay = revenueTrend.reduce(
    (best, point) => (Number(point.revenue || 0) > Number(best.revenue || 0) ? point : best),
    { date: '', revenue: 0, transactions: 0 }
  );

  return (
    <div className="admin-dashboard">
      <h2 className="admin-page-title">Dashboard</h2>
      <p className="admin-page-subtitle">Monitor bookings, users, revenue and commission at a glance.</p>

      <div className="admin-cards">
        <div className="admin-card">
          <div className="admin-card-icon bookings">
            <FaCalendarCheck />
          </div>
          <div className="admin-card-content">
            <span className="admin-card-label">Bookings (today / month)</span>
            <span className="admin-card-value">
              {d.bookingsToday ?? 0} / {d.bookingsMonth ?? 0}
            </span>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-icon users">
            <FaUsers />
          </div>
          <div className="admin-card-content">
            <span className="admin-card-label">Active guests & hosts</span>
            <span className="admin-card-value">
              {d.activeGuests ?? 0} guests / {d.activeHosts ?? 0} hosts
            </span>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-icon revenue">
            <FaRupeeSign />
          </div>
          <div className="admin-card-content">
            <span className="admin-card-label">Total revenue</span>
            <span className="admin-card-value">{formatCurrency(d.totalRevenue ?? 0)}</span>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-icon commission">
            <FaPercent />
          </div>
          <div className="admin-card-content">
            <span className="admin-card-label">App commission earned</span>
            <span className="admin-card-value">{formatCurrency(d.appCommission ?? 0)}</span>
          </div>
        </div>
      </div>

      <div className="admin-section admin-section--chart">
        <div className="admin-section-head">
          <div>
            <h3 className="admin-section-title">
              <FaChartLine /> Revenue (last 14 days)
            </h3>
            <p className="admin-section-subtitle">
              Paid stay transactions rolled up day by day for the latest 14-day window.
            </p>
          </div>

          <div className="admin-chart-kpis">
            <div className="admin-chart-kpi">
              <span className="admin-chart-kpi-label">14-day total</span>
              <strong className="admin-chart-kpi-value">{formatCurrency(revenueTrendTotal)}</strong>
            </div>

            <div className="admin-chart-kpi">
              <span className="admin-chart-kpi-label">Daily average</span>
              <strong className="admin-chart-kpi-value">{formatCurrency(revenueTrendAverage)}</strong>
            </div>

            <div className="admin-chart-kpi">
              <span className="admin-chart-kpi-label">Best day</span>
              <strong className="admin-chart-kpi-value">
                {bestRevenueDay.revenue > 0
                  ? `${formatChartDay(bestRevenueDay.date)} - ${formatCompactCurrency(bestRevenueDay.revenue)}`
                  : 'No revenue yet'}
              </strong>
            </div>
          </div>
        </div>

        <div className="admin-revenue-chart" role="img" aria-label="Bar chart showing revenue for the last 14 days">
          {revenueTrend.map((point) => {
            const revenue = Number(point.revenue || 0);
            const barHeight = revenueTrendMax > 0 ? Math.max((revenue / revenueTrendMax) * 100, revenue > 0 ? 10 : 4) : 4;

            return (
              <div className="admin-revenue-bar-group" key={point.date}>
                <span className="admin-revenue-bar-amount">{formatCompactCurrency(revenue)}</span>
                <div
                  className="admin-revenue-bar-track"
                  title={`${formatChartDay(point.date)}: ${formatCurrency(revenue)} from ${point.transactions ?? 0} transactions`}
                >
                  <div
                    className={`admin-revenue-bar-fill${revenue === 0 ? ' is-zero' : ''}`}
                    style={{ height: `${barHeight}%` }}
                  />
                </div>
                <span className="admin-revenue-bar-label">{formatChartDay(point.date)}</span>
              </div>
            );
          })}
        </div>

        {revenueTrendTotal === 0 && (
          <p className="admin-chart-empty">No paid revenue has been recorded in this 14-day range yet.</p>
        )}
      </div>

      <div className="admin-section">
        <h3 className="admin-section-title">
          <FaChartBar /> Booking & payment status
        </h3>
        <div className="admin-status-grid">
          <div className="admin-status-item">
            <span className="admin-status-label">Pending</span>
            <span className="admin-status-value">{statusCounts.pending ?? 0}</span>
          </div>
          <div className="admin-status-item confirmed">
            <span className="admin-status-label">Confirmed / Approved</span>
            <span className="admin-status-value">{(statusCounts.confirmed ?? 0) + (statusCounts.approved ?? 0)}</span>
          </div>
          <div className="admin-status-item completed">
            <span className="admin-status-label">Completed</span>
            <span className="admin-status-value">{statusCounts.completed ?? 0}</span>
          </div>
          <div className="admin-status-item declined">
            <span className="admin-status-label">Declined / Cancelled</span>
            <span className="admin-status-value">{(statusCounts.declined ?? 0) + (statusCounts.cancelled ?? 0)}</span>
          </div>
        </div>
      </div>

      <p className="admin-live-hint">Dashboard refreshes every 30 seconds. Use sidebar for detailed views.</p>
    </div>
  );
};

export default AdminDashboard;
